<template>
    <v-card :class="['session-card', { 'dashboard-alert': critical }]" outlined height="100%">
        <div class="d-flex align-center px-3 pt-2">
            <a :href="format.nowUrl(session.charger, session.plug)" target="_blank" rel="noopener" class="charger-link" title="Abrir carregador em nova aba">
                <span class="text-h6 font-weight-bold">{{ session.charger.name }}</span>
                <span class="text-subtitle-1 ml-1">({{ session.plug }})</span>
            </a>
            <v-icon v-if="online" small class="ml-2" :color="critical ? 'white' : 'green'" title="Carregador online">wifi</v-icon>
            <v-icon v-else small class="ml-2 icon-blink" :color="critical ? 'white' : 'red'" title="Carregador sem comunicação">wifi_off</v-icon>
            <v-spacer />
            <v-icon :title="unlockLabel">{{ unlockIcon }}</v-icon>
        </div>
        <div class="px-3 d-flex caption session-secondary">
            <span class="text-truncate">{{ point }}</span>
            <v-spacer />
            <span v-if="startTime" class="ml-2 text-no-wrap" :title="`Conectado em ${session.startConnected}`">
                <v-icon x-small :color="critical ? 'white' : undefined">schedule</v-icon> {{ startTime }}
            </span>
        </div>

        <div class="px-3 pt-1 d-flex align-center">
            <v-chip x-small label :color="session.errorSession ? 'white' : statusStyle.color" :text-color="session.errorSession ? 'red darken-3' : statusStyle.secondColor" class="font-weight-bold">
                <v-icon x-small left :color="session.errorSession ? 'red darken-3' : statusStyle.secondColor">{{ session.errorSession ? "error" : statusStyle.icon }}</v-icon>
                {{ session.errorSession ? "ERRO NA SESSÃO" : statusStyle.status }}
            </v-chip>
            <v-icon v-if="slowStart" small class="ml-1 icon-blink" :color="critical ? 'white' : 'red'" :title="`Liberado há ${session.minutesConnected} minutos e o carregamento não começou`">hourglass_bottom</v-icon>
            <span class="ml-2 caption text-truncate session-secondary" :title="session.user ? session.user.email : undefined">{{ userLabel }}</span>
            <v-spacer />
            <span v-if="session.transactionId" class="ml-2 caption text-no-wrap session-secondary" title="Transaction ID">#{{ session.transactionId }}</span>
        </div>

        <div v-if="paid" class="px-3 pt-1 d-flex align-center caption">
            <v-icon x-small :color="critical ? 'white' : undefined" class="mr-1">credit_card</v-icon>
            <a v-if="session.payment" class="text-truncate payment-link" :title="`Pagamento #${session.payment.id} · clique para gerenciar`" @click="$emit('open-payment', session.payment.id)">{{ money(session.payment.value) }} · {{ session.payment.operator }} · {{ paymentStatusLabel(session.payment.status) }}</a>
            <span v-else>Pagamento não encontrado</span>
            <v-spacer />
            <v-icon v-for="alert in paymentAlerts" :key="alert.key" small :class="['ml-1', { 'icon-blink': alert.critical }]" :color="critical ? 'white' : alert.critical ? 'red' : 'orange darken-2'" :title="alert.text">
                {{ alert.critical ? "credit_card_off" : "payments" }}
            </v-icon>
        </div>

        <div class="d-flex justify-space-between px-3 pt-2 pb-1 text-center">
            <div>
                <div class="text-subtitle-1 font-weight-bold">
                    <v-icon v-if="withoutConsumption" small class="icon-blink" :color="critical ? 'white' : 'red'" :title="`Conectado há ${session.minutesConnected} minutos sem consumo`">error</v-icon>
                    {{ energy }}
                </div>
                <div class="caption session-secondary">kWh</div>
            </div>
            <div>
                <div class="text-subtitle-1 font-weight-bold">
                    <v-icon v-if="lowPower" small class="icon-blink" :color="critical ? 'white' : 'red'" :title="`Potência abaixo de ${LOW_POWER_RATIO * 100}% do máximo da sessão (${format.kW(session.maximumPower)} kW)`">trending_down</v-icon>
                    {{ power }}
                </div>
                <div class="caption session-secondary">kW</div>
            </div>
            <div>
                <div class="text-subtitle-1 font-weight-bold">{{ format.time(session.minutesCharger) }}</div>
                <div class="caption session-secondary">carregando</div>
            </div>
            <div>
                <div class="text-subtitle-1 font-weight-bold">
                    <v-icon v-if="idle" small class="icon-blink" :color="critical ? 'white' : 'orange darken-2'" :title="`Ocioso há ${idleMinutes} minutos após carregar (tolerância ${idleTolerance} min)`">local_parking</v-icon>
                    {{ format.time(session.minutesConnected) }}
                </div>
                <div v-if="idle" :class="['caption', 'font-weight-bold', critical ? '' : 'orange--text text--darken-2']">ocioso {{ format.time(idleMinutes) }}</div>
                <div v-else class="caption session-secondary">conectado</div>
            </div>
        </div>

        <v-progress-linear v-if="session.percent != null" :value="session.percent" :color="critical ? 'white' : 'green'" height="16" class="mt-1">
            <span class="caption font-weight-bold">
                <template v-if="session.percentStart != null">{{ Math.round(session.percentStart) }}% → </template>{{ Math.round(session.percent) }}%
            </span>
        </v-progress-linear>
    </v-card>
</template>

<script>
import statusCharger from "@/helpers/StatusCharger";
import UnlockBy from "@/enum/UnlockBy";

import { money, paymentStatusLabel } from "@/helpers/paymentAlerts";

import { LOW_POWER_RATIO, isPaid, sessionAlerts, sessionPaymentAlerts } from "./shared/alerts";
import format from "./shared/format";

export default {
    name: "SessionCard",

    props: {
        session: { type: Object, required: true },
    },

    data() {
        return {
            format,
            LOW_POWER_RATIO,
        };
    },

    methods: {
        money,
        paymentStatusLabel,
    },

    computed: {
        alerts() {
            return sessionAlerts(this.session);
        },
        paid() {
            return isPaid(this.session);
        },
        paymentAlerts() {
            return sessionPaymentAlerts(this.session);
        },
        critical() {
            return !!this.session.errorSession || this.paymentAlerts.some((a) => a.critical);
        },
        online() {
            return this.alerts.online;
        },
        startTime() {
            // startConnected vem como "dd/mm/aaaa hh:mm:ss"; mostra a data só quando não é hoje
            const value = this.session.startConnected;
            if (!value) return null;
            return value.substr(0, 10) == this.$moment().format("DD/MM/YYYY") ? value.substr(11, 5) : value.substr(0, 16);
        },
        point() {
            return this.session.charger.point ? this.session.charger.point.name : "-";
        },
        isNfc() {
            return this.session.unlockBy == UnlockBy.NFC;
        },
        unlockIcon() {
            if (this.paid) return "credit_card";
            return this.isNfc ? "nfc" : "smartphone";
        },
        unlockLabel() {
            if (this.paid) return "Liberado por pagamento";
            return this.isNfc ? "Liberado por NFC" : "Liberado pelo aplicativo";
        },
        userLabel() {
            if (this.session.user) return format.shortEmail(this.session.user.email);
            return this.session.nfc ? `NFC ${this.session.nfc}` : "";
        },
        statusStyle() {
            return statusCharger.getComponentStyle(this.session.status);
        },
        withoutConsumption() {
            return this.alerts.withoutConsumption;
        },
        slowStart() {
            return this.alerts.slowStart;
        },
        idle() {
            return this.alerts.idle;
        },
        idleMinutes() {
            return this.alerts.idleMinutes;
        },
        idleTolerance() {
            return this.alerts.idleTolerance;
        },
        lowPower() {
            return this.alerts.lowPower;
        },
        energy() {
            return this.session.consumption ? Number(this.session.consumption).toFixed(1) : "0.0";
        },
        power() {
            return this.session.status == "Charging" && this.session.power > 0 ? format.kW(this.session.power) : "-";
        },
    },
};
</script>

<style scoped>
.charger-link {
    color: inherit;
    text-decoration: none;
}

.charger-link:hover {
    text-decoration: underline;
}

.payment-link {
    color: inherit;
}

.payment-link:hover {
    text-decoration: underline;
}

.session-secondary {
    opacity: 0.7;
}

.icon-blink {
    animation: icon-blink 1s ease-in-out infinite;
}

@keyframes icon-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.2;
    }
}
</style>
